import { useEffect } from 'react';
import { usePresenceStore, presenceActions } from '../store/presenceStore';
import { logger } from '../utils/logger';

let activePresenceHooks = 0;

/**
 * HOOK SIMPLIFIÉ POUR LA PRÉSENCE UTILISATEUR
 *
 * Remplace l'ancien hook complexe useUserPresence.
 * Utilise le store Zustand pour état centralisé et simplifié.
 *
 * @returns État de présence avec données, chargement, erreurs et actions
 *
 * @version 1.0.0
 * @since Phase 1 - Simplification Architecturale
 */
export function usePresence() {
  const {
    onlineDoctors,
    onlinePatients,
    stats,
    loadingDoctors,
    loadingPatients,
    loadingStats,
    errorDoctors,
    errorPatients,
    errorStats,
  } = usePresenceStore();
  const safeDoctors = Array.isArray(onlineDoctors) ? onlineDoctors : [];
  const safePatients = Array.isArray(onlinePatients) ? onlinePatients : [];

  // Initialisation automatique
  useEffect(() => {
    activePresenceHooks += 1;
    if (activePresenceHooks === 1) {
      logger.debug('🎯 [usePresence] Hook initialisé - démarrage store');
      presenceActions.initialize();
    } else {
      logger.debug('🎯 [usePresence] Hook initialisé - store déjà actif', { activePresenceHooks });
    }

    // Cleanup à la destruction du composant
    return () => {
      activePresenceHooks = Math.max(0, activePresenceHooks - 1);
      if (activePresenceHooks === 0) {
        logger.debug('🧹 [usePresence] Dernier hook retiré - cleanup store');
        presenceActions.cleanup();
      } else {
        logger.debug('🧹 [usePresence] Hook nettoyé', { activePresenceHooks });
      }
    };
  }, []);

  // Calcul des états dérivés
  const isLoading = loadingDoctors === 'loading' || loadingPatients === 'loading';
  const isLoadingDoctors = loadingDoctors === 'loading';
  const isLoadingPatients = loadingPatients === 'loading';

  const error = errorDoctors || errorPatients || errorStats;

  // Actions exposées
  const refreshAll = () => presenceActions.refreshAll();
  const refreshDoctors = () => presenceActions.refreshDoctors();
  const refreshPatients = () => presenceActions.refreshPatients();
  const refreshStats = () => presenceActions.refreshStats();

  return {
    // Données
    onlineDoctors: safeDoctors,
    onlinePatients: safePatients,
    stats,

    // États de chargement
    isLoading,
    isLoadingDoctors,
    isLoadingPatients,
    loadingDoctors,
    loadingPatients,
    loadingStats,

    // Erreurs
    error,
    errorDoctors,
    errorPatients,
    errorStats,

    // Actions
    refreshAll,
    refreshDoctors,
    refreshPatients,
    refreshStats,
  };
}

/**
 * HOOK SPÉCIALISÉ - DOCTEURS SEULEMENT
 */
export function usePresenceDoctors() {
  const { onlineDoctors, isLoadingDoctors, errorDoctors, refreshDoctors } = usePresence();

  return {
    doctors: onlineDoctors,
    isLoading: isLoadingDoctors,
    error: errorDoctors,
    refresh: refreshDoctors,
  };
}

/**
 * HOOK SPÉCIALISÉ - PATIENTS SEULEMENT
 */
export function usePresencePatients() {
  const { onlinePatients, isLoadingPatients, errorPatients, refreshPatients } = usePresence();

  return {
    patients: onlinePatients,
    isLoading: isLoadingPatients,
    error: errorPatients,
    refresh: refreshPatients,
  };
}

/**
 * HOOK SPÉCIALISÉ - STATISTIQUES SEULEMENT
 */
export function usePresenceStats() {
  const { stats, loadingStats, errorStats, refreshStats } = usePresence();

  return {
    stats,
    isLoading: loadingStats === 'loading',
    error: errorStats,
    refresh: refreshStats,
  };
}

export default usePresence;
